import deepEquals from "fast-deep-equal/es6";

import type { AnonCart, AnonCartContext, AnonCartItem } from "../../model/AnonCartTypes";
import type { ServiceError } from "../BaseTypes";
import type { ICartEventRepository } from "../common-cart/CartEventRepository";
import { AnonCartEventRepository } from "../common-cart/CartEventRepository";
import type { AnonCartWebsocketParams, ICartWebsocketConnector } from "../common-cart/CartWebsocketConnector";
import { AnonCartWebsocketConnector } from "../common-cart/CartWebsocketConnector";
import type { AddItemParams, ICartStore } from "../common-cart/CommonCartStore";
import { generateRequestId } from "../common-cart/CommonCartStore";
import type { CartItemChange } from "../MiniCartStore";
import type { CartItemStoreImpl } from "../common-cart/CartItemStore";
import { AnonCartItemStore } from "../common-cart/CartItemStore";
import history from "../../uidispatcherapi/BettyHistoryModule";
import { SessionStore } from "../../uidispatcherapi/SessionStore";
import { getQuantityForPopup } from "../MiniCartUtil";
import { setServiceOption } from "../../model/ServiceOptions";
import * as AnonCartServerRequests from "../../server/AnonCartServerRequests";
import { Notifications, notifyIntl } from "../../utils/notifications";
import {
	AnonCartEvent,
	applyEvents,
	CartCreatedEvent,
	CartDeletedEvent,
	isEventPending,
	ItemAddedEvent,
	ItemDeletedEvent,
	ItemQuantitySetEvent,
	ItemServiceOptionsSetEvent,
} from "./AnonCartEvents";
import { AnonCartActions } from "../../actions/AnonCartActions";
import { ErrorHandler } from "../ErrorHandler";
import { Store } from "../../utils/Store";
import { floorToThreeDecimalPlaces, minQuantity } from "../../utils/NumberUtil";
import { TrackingStoreGAUtils } from "../TrackingStoreGAUtils";
import { CommonCart } from "../../model/CommonCartTypes";
import { AnonymousCartMetaStore } from "../../uidispatcherapi/AnonymousCartMetaStore";
import {
	CommonStatus,
	emitCartEventFailureEvent,
	emitCartRequestFailureEvent,
	emitChangeItemQuantityCommandEvent,
	emitItemQuantityChangedEvent,
} from "../../events/CartEvents";
import { isAnonCartEnabled } from "../../utils/featureFlagUtil";
import { intlRoot } from "../../IntlRootMixin";

const POLLING_TIME = 750;
type AnonCartStoreInternalState = {
	confirmedCartState: AnonCart;
	events: Array<AnonCartEvent>;
	cartContext: AnonCartContext | null | undefined;
	isPending: boolean;
	loginWarningShown: boolean;
	cartVersionTimeout: number | null | undefined;
};
export class AnonCartStoreImpl extends Store implements ICartStore {
	_state!: AnonCartStoreInternalState;
	_eventRepository: ICartEventRepository<AnonCartEvent>;
	_articleDataStore: CartItemStoreImpl<AnonCartContext>;
	_websocketConnector: ICartWebsocketConnector<AnonCartWebsocketParams, AnonCartEvent>;
	_errorHandler = new ErrorHandler([
		{
			canHandle: (error) => error.code === "OC-604",
			handle: () => {
				this.resetState(getContextFromSession()).catch((error) => {
					console.error("resetState in AnonCartStore failed with ", error);
				});
			},
		},
	]);

	constructor(
		repository: ICartEventRepository<AnonCartEvent>,
		articleDataStore: CartItemStoreImpl<AnonCartContext>,
		websocketConnector: ICartWebsocketConnector<AnonCartWebsocketParams, AnonCartEvent>,
	) {
		super();
		this._eventRepository = repository;
		this._articleDataStore = articleDataStore;
		this._websocketConnector = websocketConnector;
		this.resetState(getContextFromSession()).catch((error) => {
			console.error("resetState in AnonCartStore failed with ", error);
		});
		this.listenTo(SessionStore, () => {
			this.onSessionStoreChange().catch((error) => {
				console.error("onSessionStoreChange in AnonCartStore failed with ", error);
			});
		});
		this.listenTo(AnonCartActions.addItem, (...args) => {
			const [bundleId, quantity] = args;
			this.addItem({ bundleId, quantity }).catch((error) => {
				console.error("addItem in AnonCartStore failed with ", error);
			});
		});
		this.listenTo(AnonCartActions.setItemQuantity, (...args) => {
			const [bundleId, quantity] = args;
			this.onSetItemQuantity(bundleId, quantity).catch((error) => {
				console.error("onSetItemQuantity in AnonCartStore failed with ", error);
			});
		});
		this.listenTo(AnonCartActions.setItemServiceOption, (...args) => {
			const [bundleId, serviceOptionId, selected] = args;
			this.onSetItemServiceOption(bundleId, serviceOptionId, selected).catch((error) => {
				console.error("onSetItemServiceOption in AnonCartStore failed with ", error);
			});
		});
		this.listenTo(AnonCartActions.deleteItem, (...args) => {
			const [bundleId] = args;
			this.onDeleteItem(bundleId).catch((error) => {
				console.error("onDeleteItem in AnonCartStore failed with ", error);
			});
		});
		this.listenTo(AnonCartActions.deleteItems, (...args) => {
			const [bundleIds] = args;
			this.onDeleteItems(bundleIds);
		});
		this.listenTo(AnonCartActions.deleteCart, (...args) => {
			this.onDeleteCart(args[0]).catch((error) => {
				console.error("onDeleteCart in AnonCartStore failed with ", error);
			});
		});
		this.listenTo(AnonCartActions.markCartMerged, (...args) => {
			this.onMarkCartMerged(args[0], args[1]).catch((error) => {
				console.error("onMarkCartMerged in AnonCartStore failed with ", error);
			});
		});
		this.listenTo(AnonCartActions.redirectIfNotAnonymous, this.redirectIfNotAnonymous);
		this.listenTo(articleDataStore, this.trigger);
	}

	async onSessionStoreChange() {
		const sessionContext = getContextFromSession();

		if (this.isAnonymous() && !deepEquals(this._state.cartContext, sessionContext)) {
			await this.resetState(sessionContext);
		}
	}

	async resetState(cartContext: AnonCartContext | null | undefined) {
		this._articleDataStore.refreshWatchEntries({}, "AnonCart");

		this._websocketConnector.disconnect();

		const timeout = this._state && this._state.cartVersionTimeout;

		if (timeout) {
			window.clearTimeout(timeout);
		}

		this._state = {
			confirmedCartState: getDummyCart(),
			events: [],
			cartContext: null,
			isPending: true,
			loginWarningShown: false,
			cartVersionTimeout: null,
		};

		if (this.isAnonymous() && isAnonCartEnabled() && cartContext) {
			this._state.cartContext = cartContext;

			this._websocketConnector.connect(
				cartContext,
				(event) => this.onServerConfirmation(event),
				() => this.runCheckCartVersionInLoop(),
			);

			await this.getActiveCart();
		}
	}

	// -------
	// Getters
	// -------
	getAnonCart() {
		return this._applyArticleDataToCart(
			applyEvents(this._state.confirmedCartState, this.getPendingEvents()) || getDummyCart(),
		);
	}

	getCart(): CommonCart | null {
		const cart = this.getAnonCart();
		const cartContext = this._state.cartContext;

		if (!cart || !cartContext) {
			return null;
		}

		const { country, storeId } = cartContext;
		const { cartId, cartVersion, totalPrices } = cart;

		const items = Object.fromEntries(
			Object.entries(cart.items).map(([key, item]) => [key, { ...item, orderable: true }]),
		);

		return {
			cartId,
			cartVersion,
			country,
			storeId,
			// @ts-expect-error
			items,
			totalPrices,
		};
	}

	getOrderData() {
		return null;
	}

	getDeliveryDate() {
		return null;
	}

	getPendingEvents(): Array<AnonCartEvent> {
		return this._state.events.filter((event) => event.status.type === "Pending");
	}

	getAppliedEvents(): Array<AnonCartEvent> {
		return this._state.events.filter((event) => event.status.type === "Success");
	}

	isPending(): boolean {
		return this._state.isPending;
	}

	isEmployeeCart(): boolean {
		return false;
	}

	getCartContext(): AnonCartContext | null | undefined {
		return this._state.cartContext;
	}

	// -------
	// Event handling
	// -------
	_pushEvent(event: AnonCartEvent): Promise<void> {
		this._state.events = [...this._state.events.filter((e) => e.eventId !== event.eventId), event];

		if (event.status.type !== "Pending") {
			this._state.confirmedCartState.eventHistory.push(event.eventId);
		}

		return this._eventRepository.saveEvent(this._state.confirmedCartState.cartId, event);
	}

	_loadLocalEvents(): Promise<AnonCartEvent[]> {
		return this._eventRepository.getEvents(this._state.confirmedCartState.cartId);
	}

	async _consolidateEvents(): Promise<void> {
		const historySet = new Set(this._state.confirmedCartState.eventHistory);
		const pendingEvents = new Map(this.getPendingEvents().map((event) => [event.eventId, event]));
		await Promise.all(
			[...historySet.values()]
				.map((id, seqNum) => {
					const event = pendingEvents.get(id);

					if (event) {
						return Object.assign(event, {
							status: {
								type: "Success",
							},
							seqNum: event.seqNum >= 0 ? event.seqNum : seqNum,
						});
					}

					return null;
				})
				.map((event) => event && this._eventRepository.saveEvent(this._state.confirmedCartState.cartId, event)),
		); //We should probably also retry sending pending events here.
	}

	_event2ItemChange = (
		event:
			| {
					eventId: string;
					bundleId: string;
					quantity: number;
			  }
			| null
			| undefined,
	): CartItemChange | null | undefined => {
		const cart = this.getCart();

		if (!event || !cart) {
			return null;
		}

		const item = cart.items[event.bundleId];

		if (!item) {
			return null;
		}

		const quantity = event.quantity;
		const weightPerPiece = item.weightPerPiece;
		return {
			eventId: event.eventId,
			articleDescription: item.description,
			newQuantity: getQuantityForPopup(quantity, weightPerPiece),
		};
	};
	itemChangeEventTypes: Set<string> = new Set(["ItemQuantitySetEvent", "ItemAddedEvent"]);
	isCartItemChange = (event: AnonCartEvent) => this.itemChangeEventTypes.has(event.type);

	getCartItemChange(): CartItemChange | null | undefined {
		const itemChangeEvents: Array<any> = this.getAppliedEvents().filter(this.isCartItemChange);
		return this._event2ItemChange(itemChangeEvents[itemChangeEvents.length - 1]);
	}

	_setCartAndStoreOnMetaStore(cartId: string | null, storeId: string | null) {
		AnonymousCartMetaStore.setCartId(cartId);
		AnonymousCartMetaStore.setStoreId(storeId);
	}

	async addItem({ bundleId, quantity }: AddItemParams) {
		const cartContext = this._state.cartContext;

		if (!cartContext) {
			return;
		}

		const scaledQuantity = floorToThreeDecimalPlaces(quantity);

		if (scaledQuantity < minQuantity) {
			return;
		}

		const cartId = this._state.confirmedCartState.cartId;
		const previousQuantity = this._state.confirmedCartState.items[bundleId]?.quantity ?? 0;
		const requestId = generateRequestId();
		const request = {
			requestId: requestId,
			cartContext: cartContext,
			cartId,
			bundleId: bundleId,
			quantity: scaledQuantity,
		};

		try {
			if (this._state.confirmedCartState.cartVersion === 0 && !this._state.loginWarningShown) {
				this._state.loginWarningShown = true;
				notifyIntl({
					type: "warning",
					id: "oc-anon-login",
					text: intlRoot.formatMessage({ id: "ORC_WARNING_LOGIN" }),
				});
			}

			await AnonCartServerRequests.addItem(request);
			const event = new ItemAddedEvent(requestId, cartId, bundleId, scaledQuantity, new Date().getTime());
			await this._pushEvent(event);
			emitChangeItemQuantityCommandEvent({
				cartId,
				commonStatus: "ANON" as CommonStatus,
				requestId,
				bundleId,
				previousQuantity,
				newQuantity: scaledQuantity,
			});
			this.trigger();
		} catch (e) {
			this.handleRequestError(e as ServiceError, cartId);
		}
	}

	async addItems(params: AddItemParams[]): Promise<void | null> {
		await Promise.all(params.map((it) => this.addItem(it)));
	}

	async onSetItemQuantity(bundleId: string, quantity: number) {
		const cartContext = this._state.cartContext;

		if (!cartContext) {
			return;
		}

		const cart = this.getCart();

		if (cart && !cart.items[bundleId]) {
			await this.addItem({
				bundleId,
				quantity,
			});
			return;
		}

		const scaledQuantity = floorToThreeDecimalPlaces(quantity);

		if (scaledQuantity < minQuantity) {
			await this.onDeleteItem(bundleId);
			return;
		}

		await this._setItemQuantity(bundleId, scaledQuantity, cartContext);
	}

	async _setItemQuantity(bundleId: string, quantity: number, cartContext: AnonCartContext) {
		const cartId = this._state.confirmedCartState.cartId;
		const previousQuantity = this._state.confirmedCartState.items[bundleId]?.quantity ?? 0;
		const requestId = generateRequestId();
		const request = {
			requestId: requestId,
			cartContext: cartContext,
			cartId,
			bundleId: bundleId,
			quantity,
		};
		const event = new ItemQuantitySetEvent(requestId, cartId, bundleId, quantity);

		try {
			// ultra-optimistically update for fast-clickable action
			await this._pushEvent(event);
			this.trigger();
			await AnonCartServerRequests.setItemQuantity(request);
			emitChangeItemQuantityCommandEvent({
				cartId,
				commonStatus: "ANON" as CommonStatus,
				requestId,
				bundleId,
				previousQuantity,
				newQuantity: quantity,
			});
		} catch (e) {
			// recover from ultra-optimistic update in case of failures.
			event.status = {
				type: "Failure",
				reason: "TOO_OPTIMISTIC",
			};
			await this._pushEvent(event);
			this.trigger();
			this.handleRequestError(e as any, cartId);
		}
	}

	async onSetItemServiceOption(bundleId: string, serviceOptionId: string, selected: boolean) {
		const cart = this.getCart();
		const cartContext = this._state.cartContext;

		if (!cart || !cartContext) {
			return;
		}

		const itemToChange = cart.items[bundleId];
		const serviceOption = itemToChange.serviceOptions?.find((option) => option.id === serviceOptionId);

		if (serviceOption && serviceOption.selected === selected) {
			return;
		}

		const selectedOptionIds = setServiceOption(serviceOptionId, selected, itemToChange.serviceOptions!)
			.filter((option) => option.selected)
			.map((option) => option.id);
		const cartId = this._state.confirmedCartState.cartId;
		const requestId = generateRequestId();
		const request = {
			requestId: requestId,
			cartContext: cartContext,
			cartId,
			bundleId: bundleId,
			serviceOptionIds: selectedOptionIds,
		};
		const event = new ItemServiceOptionsSetEvent(requestId, cartId, bundleId, selectedOptionIds);

		try {
			// ultra-optimistically update for fast-clickable action
			await this._pushEvent(event);
			this.trigger();
			await AnonCartServerRequests.setItemServiceOptions(request);
		} catch (e) {
			// recover from ultra-optimistic update in case of failures.
			event.status = {
				type: "Failure",
				reason: "TOO_OPTIMISTIC",
			};
			await this._pushEvent(event);
			this.trigger();
			this.handleRequestError(e as any, cartId);
		}
	}

	async onDeleteItem(bundleId: string) {
		const cartContext = this._state.cartContext;

		if (!cartContext) {
			return;
		}

		TrackingStoreGAUtils.trackGAItemRemoved(bundleId, this.getCart(), cartContext);
		const cartId = this._state.confirmedCartState.cartId;
		const previousQuantity = this._state.confirmedCartState.items[bundleId]?.quantity ?? 0;
		const requestId = generateRequestId();
		const request = {
			requestId: requestId,
			cartContext: cartContext,
			cartId,
			bundleId: bundleId,
		};

		try {
			await AnonCartServerRequests.deleteItem(request);
			const event = new ItemDeletedEvent(requestId, cartId, bundleId);
			await this._pushEvent(event);
			this.trigger();
			emitChangeItemQuantityCommandEvent({
				cartId,
				commonStatus: "ANON" as CommonStatus,
				requestId,
				bundleId,
				previousQuantity,
				newQuantity: 0,
			});
		} catch (e) {
			this.handleRequestError(e as any, cartId);
		}
	}

	onDeleteItems(bundleIds: string[]) {
		bundleIds.forEach((bundleId) =>
			this.onDeleteItem(bundleId).catch((error) => {
				console.error("onDeleteItem in AnonCartStore failed with ", error);
			}),
		);
	}

	onServerConfirmation(event: AnonCartEvent) {
		if (event.cartId !== this._state.confirmedCartState.cartId) {
			return;
		}

		if (event.seqNum !== this._state.confirmedCartState.cartVersion + 1) {
			// missed event -> discard state
			this.resetState(this._state.cartContext).catch((error) => {
				console.error("resetState in AnonCartStore failed with ", error);
			});
			return;
		}

		this._pushEvent(event).then(() => {
			const newState = applyEvents(this._state.confirmedCartState, [event]);

			if (!newState) {
				this.resetState(getContextFromSession()).catch((error) => {
					console.error("resetState in AnonCartStore failed with ", error);
				});
				return;
			}
			const oldState = this._state.confirmedCartState;
			Object.assign(this._state, {
				confirmedCartState: newState,
			});
			this._emitCustomEvents(event, oldState, newState);

			const cart = this.getAnonCart();

			this._articleDataStore.refreshWatchEntries(cart ? { ...cart.items } : {}, "AnonCart");

			const errorMessage = event.getErrorMessage();

			if (errorMessage) {
				Notifications.send(errorMessage);
			}

			this.trigger();
		});
	}

	async getActiveCart() {
		const cartContext = this._state.cartContext;

		if (!cartContext || !cartContext.country || !cartContext.storeId || !cartContext.anonUserId) {
			return;
		}

		let cartId;
		try {
			cartId = await AnonCartServerRequests.getCartId({
				cartContext,
			});

			if (cartId && cartId !== "DUMMY") {
				await this.loadCart(cartId);
				this._setCartAndStoreOnMetaStore(cartId, cartContext.storeId);
				this.trigger();
				return;
			}

			await this.createCart();
			this.trigger();
		} catch (e) {
			this.handleRequestError(e as any, cartId ?? null);
		}
	}

	static async getExplicitAnonCart(
		cartId: string,
		cartContext: AnonCartContext,
	): Promise<AnonCart | null | undefined> {
		if (cartId === "DUMMY") return null;

		try {
			return await AnonCartServerRequests.getCart({
				cartId,
				cartContext,
			});
		} catch (e) {
			return null;
		}
	}

	async loadCart(cartId: string) {
		const cartContext = this._state.cartContext;

		if (!cartContext) {
			return;
		}

		try {
			const cart = await AnonCartServerRequests.getCart({
				cartContext,
				cartId,
			});

			if (cart) {
				if (
					this._state.confirmedCartState.cartVersion < 1 &&
					cart.cartVersion > 0 &&
					!this._state.loginWarningShown
				) {
					notifyIntl({
						type: "warning",
						id: "oc-anon-login",
						text: intlRoot.formatMessage({ id: "ORC_WARNING_LOGIN" }),
					});
					this._state.loginWarningShown = true;
				}

				this._state.confirmedCartState = cart;
				this._state.events = await this._loadLocalEvents();
				await this._consolidateEvents();
				this.trigger();
			}

			this._state.isPending = false;
		} catch (e) {
			this.handleRequestError(e as any, cartId);
		}
	}

	async fetchEvents(cartId: string, eventIds: Array<string>): Promise<Array<AnonCartEvent>> {
		try {
			return await AnonCartServerRequests.getEvents({
				cartId,
				eventIds,
			});
		} catch (e) {
			this.handleRequestError(e as any, cartId);
			return [];
		}
	}

	async createCart() {
		const cartContext = this._state.cartContext;

		if (!cartContext) {
			return;
		}

		const requestId = generateRequestId();

		try {
			const cartId = await AnonCartServerRequests.createCart({
				requestId,
				cartContext,
			});
			this._state.events = [];
			this._state.confirmedCartState = { ...this._state.confirmedCartState, cartId };
			await this._pushEvent(new CartCreatedEvent(requestId, cartId));
			await this._consolidateEvents();
			this._setCartAndStoreOnMetaStore(cartId, cartContext.storeId);
			this.trigger();
			this._state.isPending = false;
		} catch (e) {
			this.handleRequestError(e as any, null);
		}
	}

	async onDeleteCart(cartId: string) {
		const cartContext = this._state.cartContext;

		if (!cartContext) {
			return;
		}

		const requestId = generateRequestId();

		try {
			await AnonCartServerRequests.deleteCart({
				requestId,
				cartContext,
				cartId,
			});
			await this._pushEvent(new CartDeletedEvent(requestId, cartId));
			this._state.events = [];
			this._state.isPending = true;
			this.trigger();
		} catch (e) {
			this.handleRequestError(e as any, cartId);
		}
	}

	onMergeFailed() {
		this._setCartAndStoreOnMetaStore(null, null);
	}

	async onMarkCartMerged(anonCart: AnonCart, targetCartId: string) {
		try {
			const requestId = generateRequestId();
			await AnonCartServerRequests.markCartMerged({
				requestId,
				cartContext: anonCart.context,
				cartId: anonCart.cartId,
				targetCartId: targetCartId,
			});
			this._setCartAndStoreOnMetaStore(null, null);
		} catch (e) {
			this.handleRequestError(e as any, anonCart.cartId);
		}
	}

	isAnonymous(): boolean {
		return SessionStore.notLoggedIn();
	}

	redirectIfNotAnonymous() {
		if (!this.isAnonymous()) {
			history.push("/cart");
		}
	}

	handleRequestError(e: ServiceError, cartId: string | null) {
		this._errorHandler.onError(null, e);

		emitCartRequestFailureEvent({
			cartId: !!cartId && cartId !== "DUMMY" ? cartId : "",
			commonStatus: "ANON",
		});
	}

	runCheckCartVersionInLoop(): void {
		const oldTimeout = this._state.cartVersionTimeout;

		if (oldTimeout) {
			window.clearTimeout(oldTimeout);
		}

		const context = this._state.cartContext;

		if (context && this.isAnonymous()) {
			this.getAndCheckCartVersion(context, this._state.confirmedCartState).catch((error) => {
				console.error("getAndCheckCartVersion in AnonCartStore failed with ", error);
			});
			const timeout = window.setTimeout(() => {
				this.runCheckCartVersionInLoop();
			}, POLLING_TIME);
			this._state = { ...this._state, cartVersionTimeout: timeout };
		}
	}

	async getAndCheckCartVersion(cartContext: AnonCartContext, cart: AnonCart) {
		if ("DUMMY" === cart.cartId) return;

		try {
			const cartId = cart.cartId;
			const serverVersion = await AnonCartServerRequests.getCartVersion({
				cartContext,
				cartId,
			});

			if (serverVersion !== cart.cartVersion) {
				if (cartId === this._state.confirmedCartState.cartId) {
					await this.updateEventsFromServer(cartId);
				}
			}
		} catch (e) {
			await this.resetState(getContextFromSession());
		}
	}

	async updateEventsFromServer(cartId: string) {
		const pendingEvents = this.getPendingEvents();

		if (pendingEvents.length > 0) {
			const pendingIds = pendingEvents.map((it) => it.eventId);
			const serverEvents = await this.fetchEvents(cartId, pendingIds);
			const appliedEvents = serverEvents && serverEvents.filter((it) => !isEventPending(it));

			if (appliedEvents && appliedEvents.length > 0) {
				appliedEvents.forEach((it) => this.onServerConfirmation(it));
			}
		}
	}

	_applyArticleDataToCart(cart: AnonCart): AnonCart {
		const articleData = this._articleDataStore.articleData;

		const items: Record<string, AnonCartItem> = Object.fromEntries(
			Object.entries(cart.items).map(([key, item]) => [
				key,
				{
					...item,
					...articleData.get(item.bundleId),
				},
			]),
		);

		return this._applyServiceOptionsSelection({ ...cart, items });
	}

	_applyServiceOptionsSelection(cart: AnonCart): AnonCart {
		const items: Record<string, AnonCartItem> = Object.fromEntries(
			Object.entries(cart.items).map(([key, item]) => {
				const serviceOptions = item.serviceOptions?.map((option) => ({
					...option,
					selected: !!item.selectedServiceOptions && item.selectedServiceOptions.includes(option.id),
				}));
				return [key, { ...item, serviceOptions }];
			}),
		);

		return { ...cart, items };
	}

	private _emitCustomEvents(event: AnonCartEvent, confirmedCartState: AnonCart, newState: AnonCart) {
		if (event.status.type == "Success") {
			if (event.type == "ItemQuantitySetEvent") {
				const bundleId = (event as ItemQuantitySetEvent).bundleId;
				emitItemQuantityChangedEvent({
					cartId: event.cartId,
					commonStatus: "ANON",
					eventId: event.eventId,
					bundleId,
					previousQuantity: confirmedCartState.items[bundleId]?.quantity ?? 0,
					newQuantity: newState.items[bundleId]?.quantity ?? (event as ItemQuantitySetEvent).quantity,
				});
			}
			if (event.type == "ItemAddedEvent") {
				const bundleId = (event as ItemAddedEvent).bundleId;
				emitItemQuantityChangedEvent({
					cartId: event.cartId,
					commonStatus: "ANON",
					eventId: event.eventId,
					bundleId,
					previousQuantity: confirmedCartState.items[bundleId]?.quantity ?? 0,
					newQuantity: newState.items[bundleId]?.quantity ?? (event as ItemQuantitySetEvent).quantity,
				});
			}
			if (event.type == "ItemDeletedEvent") {
				const bundleId = (event as ItemDeletedEvent).bundleId;
				emitItemQuantityChangedEvent({
					cartId: event.cartId,
					commonStatus: "ANON",
					eventId: event.eventId,
					bundleId,
					previousQuantity: confirmedCartState.items[bundleId]?.quantity ?? 0,
					newQuantity: newState.items[bundleId]?.quantity ?? 0,
				});
			}
		}
		if (event.status.type == "Failure") {
			emitCartEventFailureEvent({
				cartId: event.cartId,
				commonStatus: "ANON",
				eventId: event.eventId,
				reason: event.status.reason,
			});
		}
	}
}

function getContextFromSession(): AnonCartContext | null | undefined {
	const country = SessionStore.getCountry();
	const storeId = SessionStore.getSelectedStoreId() || SessionStore.getDefaultStoreForAnon();
	const anonUserId = SessionStore.getAnonymousUserId();
	const locale = SessionStore.getLocale();

	if (!country || !storeId || !anonUserId || !locale) {
		return null;
	}

	return {
		country,
		locale,
		storeId,
		anonUserId,
	};
}

function getDummyCart(): AnonCart {
	return {
		cartId: "DUMMY",
		context: {
			country: "",
			storeId: "",
			locale: "",
			anonUserId: "",
		},
		cartVersion: -1,
		items: {},
		eventHistory: [],
		totalPrices: null,
	};
}

export const AnonCartStore = new AnonCartStoreImpl(
	AnonCartEventRepository,
	AnonCartItemStore,
	AnonCartWebsocketConnector,
);
